/*
/*
 * This class is copied verbatim from Backstage 1.24.0.
 */

import { catalogApiRef } from '@backstage/plugin-catalog-react';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { useCallback, useState } from 'react';
import { BackButton, NextButton } from '../Buttons';
import { EntityListComponent } from '../EntityListComponent';
import { PrepareResult, ReviewResult } from '../useImportState';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { configApiRef, useAnalytics, useApi } from '@backstage/core-plugin-api';
import { assertError } from '@backstage/errors';
import { parseErrorMessage, parseErrorStatusCode } from '../utils';

type Props = {
  prepareResult: PrepareResult;
  onReview: (result: ReviewResult) => void;
  onGoBack?: () => void;
};

export const StepReviewLocation = ({
  prepareResult,
  onReview,
  onGoBack,
}: Props) => {
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);
  const analytics = useAnalytics();

  const appTitle = configApi.getOptionalString('app.title') || 'Witboost';

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string>();
  const exists =
    prepareResult.type === 'locations' &&
    prepareResult.locations.some(l => l.exists)
      ? true
      : false;
  const handleClick = useCallback(async () => {
    setSubmitted(true);
    analytics.captureEvent('click', 'import entity');
    try {
      let refreshed = new Array<{ target: string }>();
      if (prepareResult.type === 'locations') {
        refreshed = await Promise.all(
          prepareResult.locations
            .filter(l => l.exists)
            .map(async l => {
              const ref = stringifyEntityRef(l.entities[0] ?? l);
              await catalogApi.refreshEntity(ref).catch(e => {
                const statusCode = parseErrorStatusCode(e.message);
                if (statusCode === 403) {
                  throw new Error(
                    'You are not allowed to refresh this entity. Please check your RBAC permissions.',
                  );
                }
                throw e;
              });
              return { target: l.target };
            }),
        );
      }

      const locations = await Promise.all(
        prepareResult.locations
          .filter((l: unknown) => !(l as { exists?: boolean }).exists)
          .map(async l => {
            const result = await catalogApi.addLocation({
              type: 'url',
              target: l.target,
            });
            return {
              target: result.location.target,
              entities: result.entities,
            };
          }),
      );

      onReview({
        ...prepareResult,
        ...{ refreshed },
        locations,
      });
    } catch (e) {
      assertError(e);
      // TODO: this error should be handled differently. We add it as 'optional' and
      //       it is not uncommon that a PR has not been merged yet.
      if (
        prepareResult.type === 'repository' &&
        e.message.startsWith(
          'Location was added but has no entities specified yet',
        )
      ) {
        onReview({
          ...prepareResult,
          locations: prepareResult.locations.map(l => ({
            target: l.target,
            entities: [],
          })),
        });
      } else {
        setError(parseErrorMessage(e));
        setSubmitted(false);
      }
    }
  }, [prepareResult, onReview, catalogApi, analytics]);

  return (
    <>
      {prepareResult.type === 'repository' && (
        <>
          <Typography paragraph>
            The following Pull Request has been opened:{' '}
            <Link
              to={prepareResult.pullRequest.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {prepareResult.pullRequest.url}
            </Link>
          </Typography>

          <Typography paragraph>
            You can already import the location and {appTitle} will fetch the
            entities as soon as the Pull Request is merged.
          </Typography>
        </>
      )}

      <Typography>
        {exists
          ? 'The following locations already exist in the catalog:'
          : 'The following entities will be added to the catalog:'}
      </Typography>

      <EntityListComponent
        locations={prepareResult.locations}
        locationListItemIcon={() => <LocationOnIcon />}
      />

      {error && <FormHelperText error>{error}</FormHelperText>}

      <Grid container spacing={0}>
        {onGoBack && <BackButton onClick={onGoBack} disabled={submitted} />}
        <NextButton
          disabled={submitted}
          loading={submitted}
          onClick={() => handleClick()}
        >
          {exists ? 'Refresh' : 'Import'}
        </NextButton>
      </Grid>
    </>
  );
};
